/**
 * Module 1, côté formateur — profil et candidature (F-ONB-01, F-ONB-03), complétés le 23/09/2026 (« Modification 1 »)
 * puis allégés le 31/10/2026 (recette du dirigeant) :
 *  - un profil toujours accessible, même après validation : coordonnées, photo, structure (NDA ou « Je n'ai pas de
 *    NDA », site de la structure), domaine d'expertise en menu déroulant, profil LinkedIn. Retirés de l'écran le
 *    31/10 (conservés en base) : assurance RC, zones, langues, tarif, disponibilités, présentation, parcours ;
 *  - « Mes pièces » : CV, parcours de formation, déroulé pédagogique et récépissé de NDA obligatoires, à consulter,
 *    ouvrir, télécharger et remplacer ; autres justificatifs avec date de fin de validité (indicateur 27) ;
 *  - l'état de la candidature, sa décision et son motif.
 */
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, Check, Download, ExternalLink, FileText, Trash2 } from "lucide-react";
import { REGIONS_DREETS } from "@/domaine/pedagogie/listes";
import {
  domainesDepuisLignes,
  EXPERIENCES,
  libelleModalite,
  libellePublic,
  lignesDomaines,
  MODALITES,
  normaliserLien,
  piecesExigees,
  publicAffiche,
  PUBLICS,
  STATUTS_CANDIDATURE,
} from "@/domaine/candidature/questionnaire";
import { ParcoursCandidature } from "./CandidatureParcours";
import { api, dateFr, ErreurApi, instantFr, octets, type Candidature } from "../api";
import { useActeur, useMoi } from "../session";
import {
  Alerte,
  Bouton,
  Carte,
  Champ,
  Chargement,
  cx,
  DepotFichier,
  Etiquette,
  ListeManques,
  Onglets,
  Selecteur,
  TitrePage,
  useNotifier,
} from "../ui/base";
import { AvatarAnneau, MaPhoto } from "../ui/Avatar";
import { ReglagesAccueilProfil } from "./EspaceApprenant";
import { ChampSiret } from "../ui/ChampSiret";
import { ChampDomaines, ChoixMultiples, ListeOuAutre } from "../ui/champs";

const STATUTS: Record<string, { libelle: string; ton: "neutre" | "attente" | "accent" | "danger" }> = {
  brouillon: { libelle: "À compléter", ton: "neutre" },
  soumise: { libelle: "En cours d'étude", ton: "attente" },
  validee: { libelle: "Validée", ton: "accent" },
  refusee: { libelle: "Non retenue", ton: "danger" },
};

export function MaCandidature() {
  const acteur = useActeur();
  const requetes = useQueryClient();
  const notifier = useNotifier();
  const [onglet, setOnglet] = useState<"profil" | "pieces" | "candidature" | "accueil">(() =>
    location.hash === "#mes-pieces" ? "pieces" : location.hash === "#accueil-apprenants" ? "accueil" : "profil",
  );
  const candidature = useQuery({
    queryKey: ["candidature"],
    queryFn: () => api.get<Candidature>("/candidature"),
  });
  const maj = (c: Candidature) => {
    requetes.setQueryData(["candidature"], c);
    // 04/11/2026 : l'anneau de complétion autour de la photo (menu) suit chaque enregistrement.
    void requetes.invalidateQueries({ queryKey: ["moi"] });
  };
  const soumettre = useMutation({
    mutationFn: () => api.post<Candidature>("/candidature/soumettre"),
    onSuccess: async (c) => {
      maj(c);
      await requetes.invalidateQueries({ queryKey: ["moi"] });
      notifier("succes", "Candidature envoyée. Vous recevrez la décision par e-mail.");
    },
    onError: (e) => notifier("danger", e.message),
  });

  if (candidature.isPending) return <Chargement />;
  if (candidature.error) return <Alerte ton="danger">{candidature.error.message}</Alerte>;
  const c = candidature.data;
  const statut = STATUTS[c.formateur.statut_candidature]!;
  const valide = c.formateur.statut_candidature === "validee";
  const enEtude = c.formateur.statut_candidature === "soumise";
  const expirees = c.echeances.filter((x) => x.expiree);

  // Tant que la candidature n'est ni envoyée ni validée : le parcours guidé en 8 étapes (repris du premier SaaS).
  if (!acteur.formateur_valide && (c.formateur.statut_candidature === "brouillon" || c.formateur.statut_candidature === "refusee")) {
    return (
      <div className="mx-auto max-w-6xl">
        <TitrePage titre="Ma candidature" soustitre="Huit étapes courtes, une dizaine de minutes. L'organisme étudie votre dossier et vous répond par e-mail." actions={<Etiquette ton={statut.ton}>{statut.libelle}</Etiquette>} />
        {c.formateur.statut_candidature === "refusee" && <div className="mb-6"><Alerte ton="attention" titre="Votre candidature n'a pas été retenue en l'état">{c.formateur.motif_decision} — complétez-la puis envoyez-la à nouveau.</Alerte></div>}
        <ParcoursCandidature c={c} surMaj={maj} />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-3xl">
      <TitrePage
        titre={acteur.formateur_valide ? "Mon profil" : "Ma candidature"}
        soustitre={
          acteur.formateur_valide
            ? "Vos coordonnées, votre structure, votre domaine d'expertise et vos pièces. Tenez-les à jour : ils figurent sur vos contrats et servent au suivi qualité de l'organisme."
            : "Vos coordonnées, votre domaine d'expertise et vos pièces. L'organisme étudie le dossier et vous répond par e-mail."
        }
        actions={<Etiquette ton={statut.ton}>{statut.libelle}</Etiquette>}
      />

      {valide && !acteur.formateur_valide && (
        <div className="mb-6">
          <Alerte ton="succes" titre="Votre candidature est validée">
            Vous pouvez créer vos formations et instruire vos dossiers.
          </Alerte>
        </div>
      )}
      {enEtude && (
        <div className="mb-6">
          <Alerte titre={`Candidature envoyée le ${instantFr(c.formateur.soumise_le)}`}>
            Elle est en cours d'étude : votre profil est figé jusqu'à la décision. Vous serez prévenu par
            e-mail.
          </Alerte>
        </div>
      )}
      {c.formateur.statut_candidature === "refusee" && (
        <div className="mb-6">
          <Alerte ton="attention" titre="Votre candidature n'a pas été retenue en l'état">
            {c.formateur.motif_decision} — vous pouvez la compléter et la soumettre à nouveau.
          </Alerte>
        </div>
      )}
      {c.pieces_manquantes.length > 0 && (
        <div className="mb-6" data-testid="bandeau-pieces">
          <Alerte ton="attention" titre="Pièces à déposer">
            {valide ? "Votre profil est incomplet : " : "Il manque des pièces obligatoires : "}
            {c.pieces_manquantes.join(" · ")}.{" "}
            {onglet !== "pieces" && (
              <button
                type="button"
                className="font-semibold underline underline-offset-2"
                onClick={() => setOnglet("pieces")}
              >
                Déposer mes pièces
              </button>
            )}
          </Alerte>
        </div>
      )}
      {expirees.length > 0 && (
        <div className="mb-6">
          <Alerte ton="attention" titre="Justificatif(s) à renouveler">{expirees.map((x) => `${x.nom_fichier} (fin de validité : ${dateFr(x.expire_le)})`).join(" · ")}</Alerte>
        </div>
      )}

      <CompletudeDuProfil
        c={c}
        aller={(section) => {
          setOnglet(section === "pieces" ? "pieces" : "profil");
          window.setTimeout(() => document.getElementById(section === "pieces" ? "mes-pieces" : `profil-${section === "banque" ? "structure" : section}`)?.scrollIntoView({ block: "start", behavior: "smooth" }), 50);
        }}
      />

      <Onglets
        actif={onglet}
        choisir={setOnglet}
        onglets={[
          { cle: "profil", libelle: "Mon profil" },
          {
            cle: "pieces",
            libelle: "Mes pièces",
            compteur:
              c.pieces_manquantes.length > 0
                ? `${c.pieces_manquantes.length} à déposer`
                : String(c.pieces.length),
          },
          { cle: "candidature", libelle: "Candidature" },
          // 04/11/2026 : couverture et message d'accueil de l'espace apprenant (formateur validé seulement).
          ...(acteur.formateur_valide ? [{ cle: "accueil" as const, libelle: "Accueil des apprenants" }] : []),
        ]}
      />

      {onglet === "profil" && <Carte className="p-5"><Profil c={c} fige={enEtude} valide={valide} surMaj={maj} /></Carte>}

      {onglet === "pieces" && <MesPieces c={c} enEtude={enEtude} surMaj={maj} />}

      {onglet === "accueil" && acteur.formateur_valide && <ReglagesAccueilProfil />}

      {onglet === "candidature" && (
        <Carte className="space-y-4 p-5">
          <dl className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-6 gap-y-2 text-sm">
            <dt className="text-encre-3">Statut</dt>
            <dd><Etiquette ton={statut.ton}>{statut.libelle}</Etiquette></dd>
            <dt className="text-encre-3">Compte créé le</dt>
            <dd>{instantFr(c.formateur.cree_le)}</dd>
            <dt className="text-encre-3">Envoyée le</dt>
            <dd>{instantFr(c.formateur.soumise_le)}</dd>
            <dt className="text-encre-3">Décision le</dt>
            <dd>{instantFr(c.formateur.decidee_le)}</dd>
            {c.formateur.motif_decision && (
              <>
                <dt className="text-encre-3">Motif</dt>
                <dd>{c.formateur.motif_decision}</dd>
              </>
            )}
          </dl>
          {!valide && !enEtude && (
            <div className="space-y-4 border-t border-trait pt-4">
              <ListeManques titre="Pour envoyer votre candidature, il manque :" manques={c.manques} />
              <Bouton variante="primaire" disabled={c.manques.length > 0} enCours={soumettre.isPending} onClick={() => soumettre.mutate()}>Envoyer ma candidature</Bouton>
            </div>
          )}
          {valide && (
            <p className="text-[13px] text-encre-2">
              Votre candidature est validée. Pensez à renouveler vos attestations à leur échéance (onglet «
              Mes pièces »).
            </p>
          )}
        </Carte>
      )}
    </div>
  );
}

/**
 * Complétude du profil (04/11/2026) : anneau autour de la photo, « Profil complété à 70 % », et la liste cliquable des
 * éléments manquants (règle du noyau, `domaine/candidature/completude.ts`). Chaque élément ouvre la bonne section.
 */
function CompletudeDuProfil({ c, aller }: { c: Candidature; aller: (section: Candidature["completude"]["manquants"][number]["section"]) => void }) {
  const acteur = useActeur();
  const moi = useMoi();
  const k = c.completude;
  const complet = k.manquants.length === 0;
  return (
    <section aria-labelledby="titre-completude" className="mb-6 rounded-lg border border-trait bg-carte p-4 shadow-carte sm:p-5" data-testid="completude-profil">
      <div className="flex items-center gap-4">
        <AvatarAnneau nom={acteur.nom} photo={moi.data?.photo ?? null} taille="lg" pourcentage={k.pourcentage} />
        <div className="min-w-0">
          <h2 id="titre-completude" className="text-base font-semibold">
            Profil complété à <span className={cx("chiffres", complet ? "text-valide" : "text-encre")}>{k.pourcentage} %</span>
          </h2>
          <p className="mt-0.5 text-[13px] text-encre-2">
            {complet ? "Tout y est : votre profil alimente vos contrats et le suivi qualité de l'organisme." : `${k.faits} élément(s) sur ${k.total}. Il reste ${k.manquants.length === 1 ? "1 élément" : `${k.manquants.length} éléments`} à compléter :`}
          </p>
        </div>
      </div>
      {!complet && (
        <ul className="mt-3 flex flex-wrap gap-2" aria-label="Éléments manquants du profil">
          {k.manquants.map((m) => (
            <li key={m.cle}>
              <button
                type="button"
                onClick={() => aller(m.section)}
                className="inline-flex min-h-9 items-center gap-1.5 rounded-full border border-attente/70 bg-attente-doux px-3 py-1 text-left text-[13px] font-medium text-attente-encre hover:border-attente-encre/50"
              >
                <AlertTriangle className="size-3.5 shrink-0" aria-hidden />
                {m.libelle}
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/** Pièces du profil (31/10/2026) : obligatoires, en tête de « Mes pièces ». */
const AIDES_PIECES_PROFIL: Record<string, string> = {
  cv: "Votre CV à jour : parcours, missions, références.",
  programme: "Le programme des formations que vous proposez ou avez animées.",
  deroule: "Un déroulé pédagogique : objectifs, timing, activités, évaluations.",
  nda: "Le récépissé de votre déclaration d'activité délivré par la DREETS.",
  identite: "Carte d'identité ou passeport en cours de validité.",
  diplome: "Diplômes, titres ou certifications en lien avec votre domaine.",
};
const FORMATS_PROFIL = ".pdf,.doc,.docx,.odt,.png,.jpg,.jpeg,.webp";
const FORMATS_AUTRES = ".pdf,.png,.jpg,.jpeg,.webp,.doc,.docx,.odt,.xls,.xlsx,.ods,.csv,.txt";
const AVEC_ECHEANCE = new Set(["attestation", "kbis", "identite"]);

type Piece = Candidature["pieces"][number];

/**
 * « Mes pièces » (31/10/2026) : chaque pièce avec son nom et sa date de dépôt, à ouvrir, télécharger ou remplacer.
 * Pendant l'étude de la candidature, on peut encore AJOUTER ou REMPLACER une pièce (pas en retirer).
 */
function MesPieces({
  c,
  enEtude,
  surMaj,
}: {
  c: Candidature;
  enEtude: boolean;
  surMaj: (c: Candidature) => void;
}) {
  const notifier = useNotifier();
  const [expiration, setExpiration] = useState<Record<string, string>>({});
  const deposer = useMutation({
    mutationFn: (v: { type: string; fichier: File; remplace?: string }) =>
      api.fichier<Candidature>("/candidature/pieces", v.fichier, {
        type: v.type,
        expire_le: expiration[v.type] ?? "",
        remplace: v.remplace ?? "",
      }),
    onSuccess: (r, v) => {
      surMaj(r);
      notifier("succes", v.remplace ? "Pièce remplacée." : "Pièce déposée.");
    },
    onError: (e) => notifier("danger", e.message),
  });
  const retirer = useMutation({
    mutationFn: (id: string) => api.suppr<Candidature>(`/candidature/pieces/${id}`),
    onSuccess: surMaj,
    onError: (e) => notifier("danger", e.message),
  });
  const sansNda = c.formateur.formateur_sans_nda;
  const exigees = piecesExigees(c.formateur, c.types);
  const principales = c.types.filter((t) => t.obligatoire || t.sauf_sans_nda);
  const autres = c.types.filter((t) => !principales.includes(t));
  const enCours = (type: string, remplace?: string) =>
    deposer.isPending && deposer.variables?.type === type && deposer.variables?.remplace === remplace;

  const ligneFichier = (p: Piece, retirable: boolean) => (
    <div
      key={p.id}
      className="mt-2 flex flex-wrap items-center justify-between gap-2 rounded-sm bg-papier-2 px-3 py-2 text-[13px]"
    >
      <span className="flex min-w-0 items-center gap-2">
        <FileText className="size-4 shrink-0 text-accent" aria-hidden />
        <span className="min-w-0">
          <span className="block truncate font-medium">{p.nom_fichier}</span>
          <span className="block text-encre-3">
            Déposée le {dateFr(String(p.cree_le))} · <span className="chiffres">{octets(p.taille)}</span>
            {p.expire_le && (
              <span
                className={
                  p.expire_le < new Date().toISOString().slice(0, 10)
                    ? "ml-1 font-medium text-danger"
                    : "ml-1"
                }
              >
                {p.expire_le < new Date().toISOString().slice(0, 10) && (
                  <AlertTriangle className="mr-1 inline size-3.5" aria-hidden />
                )}
                · valable jusqu'au {dateFr(p.expire_le)}
              </span>
            )}
          </span>
        </span>
      </span>
      <span className="flex shrink-0 flex-wrap items-center gap-1">
        <a
          href={`/api/pieces-formateur/${p.id}?apercu=1`}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex min-h-8 items-center gap-1 rounded-xs px-2 text-encre-2 hover:bg-papier-3 hover:text-encre"
        >
          <ExternalLink className="size-3.5" aria-hidden />
          Ouvrir<span className="sr-only"> {p.nom_fichier} (nouvel onglet)</span>
        </a>
        <a
          href={`/api/pieces-formateur/${p.id}`}
          className="inline-flex min-h-8 items-center gap-1 rounded-xs px-2 text-encre-2 hover:bg-papier-3 hover:text-encre"
        >
          <Download className="size-3.5" aria-hidden />
          Télécharger<span className="sr-only"> {p.nom_fichier}</span>
        </a>
        <DepotFichier
          compact
          libelle="Remplacer"
          accept={principales.some((t) => t.type === p.type) ? FORMATS_PROFIL : FORMATS_AUTRES}
          enCours={enCours(p.type, p.id)}
          deposer={(fichier) => deposer.mutate({ type: p.type, fichier, remplace: p.id })}
        />
        {retirable && !enEtude && (
          <button
            type="button"
            aria-label={`Retirer ${p.nom_fichier}`}
            onClick={() => confirm(`Retirer « ${p.nom_fichier} » ?`) && retirer.mutate(p.id)}
            className="inline-flex size-8 items-center justify-center rounded-xs text-encre-3 hover:bg-papier-3 hover:text-danger"
          >
            <Trash2 className="size-3.5" aria-hidden />
          </button>
        )}
      </span>
    </div>
  );

  return (
    <div className="space-y-6" id="mes-pieces">
      <Carte className="p-5">
        <h2 className="text-base font-semibold">Pièces obligatoires</h2>
        <p className="mt-0.5 text-[13px] text-encre-2">
          PDF, Word ou image, 15 Mo maximum par fichier. Les pièces sont conservées chiffrées et transmises à
          l'organisme seul.
        </p>
        <ul className="mt-3 divide-y divide-trait">
          {principales.map((t) => {
            const pieces = c.pieces.filter((p) => p.type === t.type);
            const exigee = exigees.some((e) => e.type === t.type);
            return (
              <li key={t.type} className="py-3" data-piece={t.type}>
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="flex flex-wrap items-center gap-2 text-sm font-medium">
                      {pieces.length > 0 ? (
                        <Check className="size-4 text-valide" aria-hidden />
                      ) : exigee ? (
                        <AlertTriangle className="size-4 text-attente-encre" aria-hidden />
                      ) : (
                        <FileText className="size-4 text-encre-3" aria-hidden />
                      )}
                      {t.libelle}
                      {pieces.length > 0 ? (
                        <Etiquette ton="accent">Déposée</Etiquette>
                      ) : exigee ? (
                        <Etiquette ton="attente">À déposer</Etiquette>
                      ) : (
                        <Etiquette>Non requise</Etiquette>
                      )}
                    </p>
                    <p className="mt-0.5 text-[13px] text-encre-3">
                      {t.type === "nda" && sansNda
                        ? "Non requise : vous avez indiqué ne pas avoir de NDA (onglet « Mon profil »)."
                        : AIDES_PIECES_PROFIL[t.type]}
                    </p>
                  </div>
                  {pieces.length === 0 && (
                    <DepotFichier
                      compact
                      libelle="Déposer"
                      accept={FORMATS_PROFIL}
                      enCours={enCours(t.type)}
                      deposer={(fichier) => deposer.mutate({ type: t.type, fichier })}
                    />
                  )}
                </div>
                {pieces.map((p) => ligneFichier(p, pieces.length > 1))}
              </li>
            );
          })}
        </ul>
      </Carte>

      <Carte className="p-5">
        <h2 className="text-base font-semibold">Autres justificatifs</h2>
        <p className="mt-0.5 text-[13px] text-encre-2">
          Facultatifs. Pour une attestation (URSSAF…) ou un K-bis, indiquez la date de fin de validité :
          l'application vous préviendra.
        </p>
        <ul className="mt-3 divide-y divide-trait">
          {autres.map((t) => {
            const pieces = c.pieces.filter((p) => p.type === t.type);
            return (
              <li key={t.type} className="py-3">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <p className="flex items-center gap-2 text-sm font-medium">
                    {pieces.length > 0 && <Check className="size-4 text-valide" aria-hidden />}
                    {t.libelle}
                  </p>
                  <span className="flex flex-wrap items-center gap-2">
                    {AVEC_ECHEANCE.has(t.type) && (
                      <label className="flex items-center gap-1.5 text-[12.5px] text-encre-3">
                        Valable jusqu'au
                        <input type="date" value={expiration[t.type] ?? ""} onChange={(e) => setExpiration({ ...expiration, [t.type]: e.target.value })} className="h-8 rounded-sm border border-trait-fort bg-carte px-2 text-[13px]" />
                      </label>
                    )}
                    <DepotFichier
                      compact
                      libelle="Ajouter"
                      accept={FORMATS_AUTRES}
                      enCours={enCours(t.type)}
                      deposer={(fichier) => deposer.mutate({ type: t.type, fichier })}
                    />
                  </span>
                </div>
                {pieces.map((p) => ligneFichier(p, true))}
              </li>
            );
          })}
        </ul>
      </Carte>
    </div>
  );
}

function Profil({ c, fige, valide, surMaj }: { c: Candidature; fige: boolean; valide: boolean; surMaj: (c: Candidature) => void }) {
  const notifier = useNotifier();
  const moi = useMoi();
  const f = c.formateur;
  const [v, setV] = useState({
    formateur_prenom: f.formateur_prenom, formateur_nom: f.formateur_nom, formateur_telephone: f.formateur_telephone,
    formateur_statut_juridique: f.formateur_statut_juridique,
    formateur_entreprise_nom: f.formateur_entreprise_nom,
    formateur_entreprise_adresse: f.formateur_entreprise_adresse,
    formateur_entreprise_siret: f.formateur_entreprise_siret,
    formateur_nda_numero: f.formateur_nda_numero,
    formateur_sans_nda: f.formateur_sans_nda,
    formateur_dreets_region: f.formateur_dreets_region,
    formateur_iban: f.formateur_iban,
    formateur_bic: f.formateur_bic,
    formateur_site_structure: f.formateur_site_structure,
    formateur_linkedin: f.formateur_linkedin,
    formateur_specialite: f.formateur_specialite,
    formateur_experience: f.formateur_experience,
    formateur_publics: (f.formateur_publics ?? []) as string[],
    formateur_modalites: (f.formateur_modalites ?? []) as string[],
    engagement_qualite: f.engagement_qualite_le !== null,
  });
  const [domaines, setDomaines] = useState(() =>
    lignesDomaines((f.formateur_domaines ?? []) as string[], f.formateur_domaine_autre),
  );
  // Revue I-3 : les formateurs validés avant le questionnaire du 30/10/2026 n'avaient aucun moyen de prendre l'engagement
  // qualité, d'où un jalon « Profil complet » inatteignable. La date est posée par le serveur ; une fois validé, il ne se retire plus.
  const engagementAcquis = valide && f.engagement_qualite_le !== null;
  const enregistrer = useMutation({
    mutationFn: () =>
      api.patch<Candidature>("/candidature", {
        ...v,
        ...domainesDepuisLignes(domaines),
        formateur_linkedin: normaliserLien(v.formateur_linkedin),
        formateur_site_structure: normaliserLien(v.formateur_site_structure),
      }),
    onSuccess: (r) => { surMaj(r); notifier("succes", "Profil enregistré."); },
  });
  const err = enregistrer.error instanceof ErreurApi ? (enregistrer.error.details?.champs ?? {}) : {};
  type CleTexte = Exclude<
    keyof typeof v,
    "formateur_publics" | "formateur_modalites" | "engagement_qualite" | "formateur_sans_nda"
  >;
  const champ = (cle: CleTexte) => ({
    value: v[cle],
    erreur: err[cle],
    disabled: fige || (valide && (cle === "formateur_prenom" || cle === "formateur_nom")),
    onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      setV({ ...v, [cle]: e.target.value }),
  });
  const domainesValeurs = domainesDepuisLignes(domaines).formateur_domaines;
  const erreurDomaine =
    domainesValeurs.length === 0
      ? undefined
      : domainesValeurs.includes("Autre")
        ? "Précisez le domaine « Autre »."
        : undefined;

  return (
    <form
      noValidate // « linkedin.com/in/… » sans https:// est complété avant l'envoi ; le serveur contrôle tout
      className="space-y-4"
      onSubmit={(e) => {
        e.preventDefault();
        enregistrer.mutate();
      }}
    >
      <h2 id="profil-photo" className="scroll-mt-20 text-base font-semibold">Photo de profil</h2>
      <MaPhoto nom={`${f.formateur_prenom} ${f.formateur_nom}`} photo={moi.data?.photo ?? null} />

      <h2 id="profil-coordonnees" className="scroll-mt-20 pt-2 text-base font-semibold">Coordonnées</h2>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <Champ libelle="Prénom" required {...champ("formateur_prenom")} />
        <Champ libelle="Nom" required {...champ("formateur_nom")} />
        <Champ libelle="Téléphone" type="tel" {...champ("formateur_telephone")} />
      </div>
      {valide && <p className="-mt-2 text-[12.5px] text-encre-3">Nom et prénom figurent sur vos contrats signés : ils ne se modifient que par l'organisme.</p>}

      <h2 id="profil-structure" className="scroll-mt-20 pt-2 text-base font-semibold">Votre structure</h2>
      <p className="-mt-2 text-[13px] text-encre-3">
        Ces informations figurent sur le contrat de sous-traitance et sur vos factures.
      </p>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <ListeOuAutre libelle="Statut juridique" options={STATUTS_CANDIDATURE} value={v.formateur_statut_juridique} onChange={(x) => setV({ ...v, formateur_statut_juridique: x })} autre={false} disabled={fige} />
        <Champ libelle="Raison sociale" {...champ("formateur_entreprise_nom")} />
        <ChampSiret value={v.formateur_entreprise_siret} erreur={err.formateur_entreprise_siret} disabled={fige} onChange={(x) => setV((y) => ({ ...y, formateur_entreprise_siret: x }))} onChoisir={(e) => setV((y) => ({ ...y, formateur_entreprise_siret: e.siret, formateur_entreprise_nom: e.raison_sociale, formateur_entreprise_adresse: e.adresse || y.formateur_entreprise_adresse }))} />
        <Champ libelle="Adresse" {...champ("formateur_entreprise_adresse")} />
        <div className="space-y-2">
          <Champ
            libelle="N° de déclaration d'activité (NDA)"
            inputMode="numeric"
            placeholder="11 chiffres"
            aide={v.formateur_sans_nda ? undefined : "Le récépissé se dépose dans « Mes pièces »."}
            {...champ("formateur_nda_numero")}
            disabled={fige || v.formateur_sans_nda}
          />
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={v.formateur_sans_nda}
              disabled={fige}
              onChange={(e) =>
                setV({
                  ...v,
                  formateur_sans_nda: e.target.checked,
                  ...(e.target.checked ? { formateur_nda_numero: "", formateur_dreets_region: "" } : {}),
                })
              }
              className="size-4 accent-[var(--color-accent)]"
            />
            Je n'ai pas de NDA
          </label>
          {v.formateur_sans_nda && (
            <p className="text-[12.5px] text-encre-3">
              Avec le portage, vous formez sous la déclaration de l'organisme : ni numéro ni récépissé ne vous
              sont demandés.
            </p>
          )}
        </div>
        {!v.formateur_sans_nda ? (
          <ListeOuAutre
            libelle="Région de la DREETS"
            options={REGIONS_DREETS}
            value={v.formateur_dreets_region}
            onChange={(x) => setV({ ...v, formateur_dreets_region: x })}
            autre={false}
            disabled={fige}
          />
        ) : (
          <span className="hidden sm:block" />
        )}
        <Champ libelle="IBAN" {...champ("formateur_iban")} />
        <Champ libelle="BIC" {...champ("formateur_bic")} />
      </div>
      <Champ
        libelle="Site de la structure (facultatif)"
        type="url"
        inputMode="url"
        maxLength={300}
        placeholder="www.ma-structure.fr"
        {...champ("formateur_site_structure")}
      />

      <h2 id="profil-expertise" className="scroll-mt-20 pt-2 text-base font-semibold">Votre expertise</h2>
      <ChampDomaines
        lignes={domaines}
        onChange={setDomaines}
        erreur={err.formateur_domaines ?? erreurDomaine}
        disabled={fige}
      />
      <Champ libelle="Spécialité en une phrase" maxLength={200} {...champ("formateur_specialite")} />
      <Selecteur libelle="Ancienneté de formateur" value={v.formateur_experience} disabled={fige} onChange={(e) => setV({ ...v, formateur_experience: e.target.value })}>
        <option value="">— Choisir —</option>
        {EXPERIENCES.map((x) => <option key={x.valeur} value={x.valeur}>{x.libelle}</option>)}
      </Selecteur>
      <ChoixMultiples
        libelle="Publics formés"
        options={PUBLICS.map((x) => x.libelle)}
        valeurs={v.formateur_publics.filter(publicAffiche).map(libellePublic)}
        onChange={(x) =>
          !fige &&
          setV({
            ...v,
            formateur_publics: [
              ...PUBLICS.filter((p) => x.includes(p.libelle)).map((p) => p.valeur as string),
              ...v.formateur_publics.filter((p) => !publicAffiche(p)),
            ],
          })
        }
      />
      <ChoixMultiples
        libelle="Modalités"
        options={MODALITES.map((x) => x.libelle)}
        valeurs={v.formateur_modalites.map(libelleModalite)}
        onChange={(x) =>
          !fige &&
          setV({
            ...v,
            formateur_modalites: MODALITES.filter((m) => x.includes(m.libelle)).map((m) => m.valeur),
          })
        }
      />
      <Champ
        libelle="Profil LinkedIn"
        type="url"
        inputMode="url"
        maxLength={300}
        placeholder="linkedin.com/in/votre-nom"
        {...champ("formateur_linkedin")}
      />

      <h2 id="profil-engagement" className="scroll-mt-20 pt-2 text-base font-semibold">Engagement qualité</h2>
      <label className={cx("flex gap-3 rounded-md border p-4 text-sm leading-relaxed has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-accent/30", v.engagement_qualite ? "border-accent bg-accent-doux" : "border-trait-fort", engagementAcquis || fige ? "cursor-default" : "cursor-pointer")}>
        <input type="checkbox" checked={v.engagement_qualite} disabled={fige || engagementAcquis} onChange={(e) => setV({ ...v, engagement_qualite: e.target.checked })} className="mt-0.5 size-5 shrink-0 accent-[var(--color-accent)]" />
        <span>
          Je m'engage à respecter le référentiel national qualité <strong>Qualiopi</strong> et le code de déontologie de l'organisme, à m'exprimer de manière sincère et à répondre aux demandes de l'organisme en cas de contrôle.
          {f.engagement_qualite_le && <span className="mt-1 block text-[12.5px] text-encre-3">Engagement pris le {dateFr(f.engagement_qualite_le)}.</span>}
        </span>
      </label>
      {err.engagement_qualite && <p className="-mt-2 text-[13px] text-danger">{err.engagement_qualite}</p>}
      {enregistrer.error && Object.keys(err).length === 0 && <Alerte ton="danger">{enregistrer.error.message}</Alerte>}
      <Bouton type="submit" variante="primaire" disabled={fige} enCours={enregistrer.isPending}>Enregistrer mon profil</Bouton>
    </form>
  );
}
